import { format } from 'date-fns';
import type { CalendarServiceItem } from '@/types';

/** Gap (ms) between two jobs that still reads as one continuous visit. */
const TOUCH_TOLERANCE_MS = 60_000;

const sumOptional = (items: CalendarServiceItem[], pick: (item: CalendarServiceItem) => number | undefined) =>
  items.some((item) => pick(item) !== undefined)
    ? items.reduce((total, item) => total + (pick(item) ?? 0), 0)
    : undefined;

type Run = [CalendarServiceItem, ...CalendarServiceItem[]];

function combine(items: Run): CalendarServiceItem {
  const [first] = items;
  if (items.length === 1) return first;
  const start = Math.min(...items.map((item) => new Date(item.scheduledStart).getTime()));
  const end = Math.max(...items.map((item) => new Date(item.scheduledEnd).getTime()));
  const plannedHours = items.reduce((total, item) => total + (item.plannedHours ?? item.durationHours), 0);
  const actualDurationHours = sumOptional(items, (item) => item.actualDurationHours);
  const actualActiveHours = sumOptional(items, (item) => item.actualActiveHours);
  return {
    ...first,
    id: `merged:${items.map((item) => item.id).join('+')}`,
    scheduledStart: new Date(start).toISOString(),
    scheduledEnd: new Date(end).toISOString(),
    durationHours: items.reduce((total, item) => total + item.durationHours, 0),
    plannedHours,
    ...(actualDurationHours !== undefined && { actualDurationHours }),
    ...(actualActiveHours !== undefined && { actualActiveHours }),
    schedulingType: items.some((item) => item.schedulingType === 'exact') ? 'exact' : first.schedulingType ?? 'floating',
    isContinuationSegment: items.every((item) => item.isContinuationSegment),
    mergedItems: items,
  };
}

/**
 * One mechanic's back-to-back jobs on the same work order collapse into a
 * single calendar entry; the tooltip lists the individual jobs. Only jobs that
 * touch or overlap on the same local day merge — a job later in the day or on
 * another day stays its own entry so nothing appears where no work is planned.
 * Unassigned and tentative items are never merged.
 */
export function mergeWorkOrderItems(items: readonly CalendarServiceItem[]): CalendarServiceItem[] {
  const passthrough: CalendarServiceItem[] = [];
  const buckets = new Map<string, CalendarServiceItem[]>();
  for (const item of items) {
    if (!item.assignedMechanicId || item.isTentative || item.isDateOnly) {
      passthrough.push(item);
      continue;
    }
    const key = `${item.assignedMechanicId}|${item.bookingId}|${format(new Date(item.scheduledStart), 'yyyy-MM-dd')}`;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(item);
    else buckets.set(key, [item]);
  }

  const merged: CalendarServiceItem[] = [];
  for (const bucket of buckets.values()) {
    const sorted = [...bucket].sort((a, b) =>
      new Date(a.scheduledStart).getTime() - new Date(b.scheduledStart).getTime());
    let run: Run | null = null;
    let runEnd = -Infinity;
    for (const item of sorted) {
      const start = new Date(item.scheduledStart).getTime();
      if (run && start - runEnd <= TOUCH_TOLERANCE_MS) {
        run.push(item);
      } else {
        if (run) merged.push(combine(run));
        run = [item];
        runEnd = -Infinity;
      }
      runEnd = Math.max(runEnd, new Date(item.scheduledEnd).getTime());
    }
    if (run) merged.push(combine(run));
  }
  return [...passthrough, ...merged];
}

/** An entry that stands for several jobs (see {@link mergeWorkOrderItems}). */
export function isMergedItem(item: CalendarServiceItem): boolean {
  return (item.mergedItems?.length ?? 0) > 1;
}

/** One job's new start when a calendar entry is dropped. */
export interface ServiceMove {
  serviceItemId: string;
  newStart: Date;
}

/**
 * The jobs a merged entry moves when it is dropped at `newStart`, earliest
 * first. Every job shifts by the same amount, so their order and gaps stay as
 * they were. A job is moved by its first segment; a continuation whose first
 * segment lies outside the entry cannot be moved from here, so the entry as a
 * whole is not movable (`null`).
 */
export function mergedItemMoves(item: CalendarServiceItem, newStart: Date): ServiceMove[] | null {
  const parts = item.mergedItems ?? [item];
  const entryStart = new Date(item.scheduledStart).getTime();
  const heads = new Map<string, number>();
  for (const part of parts) {
    if (part.isContinuationSegment) continue;
    const start = new Date(part.scheduledStart).getTime();
    const seen = heads.get(part.sourceId);
    if (seen === undefined || start < seen) heads.set(part.sourceId, start);
  }
  if (parts.some((part) => !heads.has(part.sourceId))) return null;
  return [...heads.entries()]
    .sort(([, a], [, b]) => a - b)
    .map(([serviceItemId, start]) => ({
      serviceItemId,
      newStart: new Date(newStart.getTime() + start - entryStart),
    }));
}
